/**
 * Domain selector dropdown for HomePage header
 */

import { useEffect } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { resolveAllowedDomain } from "./constants";
import { useAllowedDomainOptions } from "./useAllowedDomainOptions";

interface DomainSelectorProps {
  domain: string;
  onDomainChange: (domain: string) => void;
  isDark: boolean;
  disabled?: boolean;
  title?: string;
}

export function DomainSelector({
  domain,
  onDomainChange,
  isDark,
  disabled = false,
  title,
}: DomainSelectorProps) {
  const { options, privilegesLoaded } = useAllowedDomainOptions();
  const selectValue = resolveAllowedDomain(domain, options) ?? "";

  // Persist fallback when the store still holds a domain the user cannot view
  // (e.g. default/session MPR without gri.regulation.view_mpr).
  useEffect(() => {
    if (!privilegesLoaded || options.length === 0) return;
    if (selectValue && selectValue !== domain) {
      onDomainChange(selectValue);
    }
  }, [domain, onDomainChange, options.length, privilegesLoaded, selectValue]);

  return (
    <FormControl size="small" sx={{ minWidth: 120 }} title={title}>
      <Select
        value={selectValue}
        disabled={disabled || options.length === 0}
        onChange={(e) => onDomainChange(e.target.value)}
        renderValue={(value) => {
          const option = options.find((opt) => opt.value === value);
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {option && (
                <img
                  src={option.icon}
                  alt={option.label}
                  style={{ width: "20px", height: "20px" }}
                />
              )}
              <span>{value}</span>
            </div>
          );
        }}
        sx={{
          backgroundColor: isDark ? "#1e293b" : "#fff",
          color: isDark ? "#fff" : "#0f172a",
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                backgroundColor: isDark ? "#1e293b" : "#fff",
                "& .MuiMenuItem-root": {
                  color: isDark ? "#fff" : "#0f172a",
                  "&:hover": {
                    backgroundColor: isDark ? "#334155" : "#f1f5f9",
                  },
                  "&.Mui-selected": {
                    backgroundColor: isDark ? "#334155" : "#e2e8f0",
                    "&:hover": {
                      backgroundColor: isDark ? "#475569" : "#cbd5e1",
                    },
                  },
                },
                "& .domain-header": {
                  fontWeight: 400,
                  fontSize: "0.7rem",
                  color: isDark ? "#94a3b8" : "#64748b",
                  pointerEvents: "none",
                  paddingTop: "12px",
                  paddingBottom: "8px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  borderBottom: `1px solid ${
                    isDark ? "#334155" : "#e2e8f0"
                  }`,
                },
              },
            },
          },
        }}
      >
        <MenuItem disabled className="domain-header">
          Domain(s)
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <img
                src={option.icon}
                alt={option.label}
                style={{ width: "20px", height: "20px" }}
              />
              <span>{option.label}</span>
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
